import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Badge
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faDownload,
  faBoxOpen
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import apiClient from 'api/client';
import { categoryApi } from 'api/endpoints/Category';

// ── API calls ────────────────────────────────────────────────────────────────
const STOCK_REPORT_URL = '/digitization/v1/stock-in/my-stock-report';
const WAREHOUSE_URL = '/traceability/v1/center/get_warehouse_list';
const ORG_URL = '/digitization/v1/organization/list';
const PRODUCT_REPORT_URL = '/digitization/v1/product/report';

const fetchStockReport = params => apiClient.post(STOCK_REPORT_URL, params);
const fetchWarehouses = () => apiClient.get(WAREHOUSE_URL);
const fetchOrganizations = () => apiClient.get(ORG_URL);
const fetchCategories = () => categoryApi.getCategoryList();
const fetchProducts = params => apiClient.post(PRODUCT_REPORT_URL, params);

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = n => Number(n || 0).toLocaleString();
const fmtUGX = n => `${Math.round(Number(n) || 0).toLocaleString()} UGX`;

const BADGE_COLOURS = [
  'primary',
  'success',
  'info',
  'warning',
  'secondary',
  'danger'
];
const catBadge = idx => BADGE_COLOURS[idx % BADGE_COLOURS.length];

// ── component ─────────────────────────────────────────────────────────────────
export default function MyStockReport() {
  // ── filter state ─────────────────────────────────────────────────────────
  const [orgId, setOrgId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [productId, setProductId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ── dropdown data ─────────────────────────────────────────────────────────
  const [orgs, setOrgs] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  // ── table data ────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // ── load master data on mount ─────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const [orgRes, whRes, catRes] = await Promise.all([
          fetchOrganizations(),
          fetchWarehouses(),
          fetchCategories()
        ]);
        setOrgs(orgRes.data?.results || orgRes.data || []);
        setWarehouses(whRes.data?.results || whRes.data || []);
        setCategories(catRes.data?.results || []);
      } catch (err) {
        console.error('Failed to load filters:', err);
      }

      // Auto-load report data on mount
      setLoading(true);
      setSearched(true);
      try {
        const res = await fetchStockReport({
          organization_id: null,
          warehouse_id: null,
          category_id: null,
          product_id: null,
          start_date: null,
          end_date: null
        });
        setRows(res.data?.results?.data || []);
      } catch (err) {
        console.error('Failed to auto-load stock report:', err);
        toast.error('Failed to load stock report data');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // ── load products when category changes ───────────────────────────────────
  useEffect(() => {
    if (!categoryId) {
      setProducts([]);
      setProductId('');
      return;
    }
    const loadProducts = async () => {
      try {
        const res = await fetchProducts({
          cursor: 0,
          limit: 200,
          filters: { search_text: '', start_date: '', end_date: '' },
          category_id: categoryId
        });
        setProducts(res.data?.results?.data || res.data?.results || []);
        setProductId('');
      } catch (_) {
        setProducts([]);
      }
    };
    loadProducts();
  }, [categoryId]);

  // ── run report ────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    setLoading(true);
    setSearched(true);
    try {
      const res = await fetchStockReport({
        organization_id: orgId || null,
        warehouse_id: warehouseId || null,
        category_id: categoryId || null,
        product_id: productId || null,
        start_date: startDate || null,
        end_date: endDate || null
      });
      setRows(res.data?.results?.data || []);
    } catch (err) {
      toast.error('Failed to load stock report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, warehouseId, categoryId, productId, startDate, endDate]);

  // ── clear filters ─────────────────────────────────────────────────────────
  const handleClear = async () => {
    setOrgId('');
    setWarehouseId('');
    setCategoryId('');
    setProductId('');
    setStartDate('');
    setEndDate('');

    setLoading(true);
    setSearched(true);
    try {
      const res = await fetchStockReport({
        organization_id: null,
        warehouse_id: null,
        category_id: null,
        product_id: null,
        start_date: null,
        end_date: null
      });
      setRows(res.data?.results?.data || []);
    } catch (err) {
      toast.error('Failed to load stock report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!rows.length) {
      toast.warning('No data to export');
      return;
    }
    const headers = Object.keys(rows[0]).filter(
      k =>
        ![
          'organization_id',
          'warehouse_id',
          'product_category_id',
          'product_id'
        ].includes(k)
    );
    const csv = [
      headers.join(','),
      ...rows.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MyStockReport_${new Date()
      .toISOString()
      .substring(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── totals ────────────────────────────────────────────────────────────────
  const totalStockIn = rows.reduce((s, r) => s + Number(r['Stock In'] || 0), 0);
  const totalStockOut = rows.reduce(
    (s, r) => s + Number(r['Stock Out'] || 0),
    0
  );
  const totalCurrent = rows.reduce(
    (s, r) => s + Number(r['Current Stock Qty'] || 0),
    0
  );
  const totalValue = rows.reduce(
    (s, r) => s + Number(r['Total Stock in Value'] || 0),
    0
  );

  // ── style constants ───────────────────────────────────────────────────────
  const inputStyle = {
    borderRadius: '8px',
    padding: '8px 12px',
    border: '1.5px solid #D8E0F0',
    fontSize: '13px'
  };

  const COLUMNS = [
    'Organization',
    'Warehouse',
    'Product',
    'Product Category',
    'Option Name',
    'Unit',
    'Lot ID',
    'Best Before',
    'Stock In',
    'Stock Out',
    'Current Stock Qty',
    'Product Price',
    'Total Stock in Value'
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Container
      fluid
      className="py-4 px-4"
      style={{ backgroundColor: '#F4F6FB', minHeight: '100vh' }}
    >
      {/* ── page header ── */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-1 fw-bold text-dark" style={{ fontSize: '22px' }}>
            My Stock Report
          </h4>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            Inventory &gt; My Stock Report
          </div>
        </div>
        <Button
          variant="outline-success"
          className="rounded-pill d-flex align-items-center gap-2"
          style={{ fontSize: '13px', padding: '7px 18px' }}
          onClick={handleExport}
          disabled={!rows.length}
        >
          <FontAwesomeIcon icon={faDownload} /> Export CSV
        </Button>
      </div>

      {/* ── FILTER CARD ── */}
      <Card
        className="shadow-sm border-0 mb-4"
        style={{ borderRadius: '14px' }}
      >
        <Card.Body className="p-4">
          <h6 className="fw-bold text-dark mb-3" style={{ fontSize: '14px' }}>
            Filter / Search
          </h6>
          <Row className="g-3 align-items-end">
            {/* Organization */}
            <Col md={3} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                Organization
              </Form.Label>
              <Form.Select
                value={orgId}
                onChange={e => setOrgId(e.target.value)}
                style={inputStyle}
              >
                <option value="">All Organizations</option>
                {orgs.map(o => (
                  <option
                    key={o.organization_id || o.value}
                    value={o.organization_id || o.value}
                  >
                    {o.organization_name || o.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Warehouse */}
            <Col md={3} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                Warehouse
              </Form.Label>
              <Form.Select
                value={warehouseId}
                onChange={e => setWarehouseId(e.target.value)}
                style={inputStyle}
              >
                <option value="">All Warehouses</option>
                {warehouses.map(w => (
                  <option key={w.id || w.value} value={w.id || w.value}>
                    {w.name || w.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Start Date */}
            <Col md={2} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                Start Date
              </Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={inputStyle}
              />
            </Col>

            {/* End Date */}
            <Col md={2} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                End Date
              </Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={inputStyle}
              />
            </Col>

            {/* Product Category */}
            <Col md={3} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                Product Category
              </Form.Label>
              <Form.Select
                value={categoryId}
                onChange={e => setCategoryId(e.target.value)}
                style={inputStyle}
              >
                <option value="">All Categories</option>
                {categories.map(c => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Product */}
            <Col md={3} sm={6}>
              <Form.Label
                className="fw-semibold text-secondary mb-1"
                style={{ fontSize: '12px' }}
              >
                Product
              </Form.Label>
              <Form.Select
                value={productId}
                onChange={e => setProductId(e.target.value)}
                disabled={!categoryId}
                style={inputStyle}
              >
                <option value="">All Products</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>
                    {p['Product Name'] || p.product_name || p.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Action buttons */}
            <Col md={4} sm={12}>
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  className="rounded-pill d-flex align-items-center gap-2 px-4"
                  style={{ fontSize: '13px', padding: '9px 20px' }}
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <FontAwesomeIcon icon={faSearch} />
                  )}
                  {loading ? 'Loading…' : 'Search'}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="rounded-pill d-flex align-items-center gap-2"
                  style={{ fontSize: '13px', padding: '9px 16px' }}
                  onClick={handleClear}
                >
                  <FontAwesomeIcon icon={faTimes} /> Clear
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── SUMMARY TILES (only after search) ── */}
      {searched && !loading && rows.length > 0 && (
        <Row className="g-3 mb-4">
          {[
            {
              label: 'Total Products',
              value: rows.length,
              colour: '#6366F1',
              bg: '#EEF2FF'
            },
            {
              label: 'Total Stock In',
              value: fmt(totalStockIn),
              colour: '#16A34A',
              bg: '#F0FDF4'
            },
            {
              label: 'Total Stock Out',
              value: fmt(totalStockOut),
              colour: '#DC2626',
              bg: '#FEF2F2'
            },
            {
              label: 'Current Stock Qty',
              value: fmt(totalCurrent),
              colour: '#D97706',
              bg: '#FFFBEB'
            },
            {
              label: 'Total Value (UGX)',
              value: fmtUGX(totalValue),
              colour: '#0EA5E9',
              bg: '#F0F9FF'
            }
          ].map(tile => (
            <Col key={tile.label} md={2} sm={6} xs={12}>
              <Card
                className="border-0 shadow-sm h-100"
                style={{ borderRadius: '12px', backgroundColor: tile.bg }}
              >
                <Card.Body className="p-3">
                  <div
                    className="text-muted mb-1"
                    style={{
                      fontSize: '11px',
                      fontWeight: 500,
                      textTransform: 'uppercase'
                    }}
                  >
                    {tile.label}
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: '18px', color: tile.colour }}
                  >
                    {tile.value}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ── TABLE CARD ── */}
      <Card className="shadow-sm border-0" style={{ borderRadius: '14px' }}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading stock data…</p>
            </div>
          ) : !searched ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon
                icon={faBoxOpen}
                size="3x"
                className="mb-3 text-secondary"
                style={{ opacity: 0.4 }}
              />
              <p style={{ fontSize: '15px' }}>
                Select filters and click <strong>Search</strong> to view stock
                data
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon
                icon={faBoxOpen}
                size="3x"
                className="mb-3"
                style={{ opacity: 0.35 }}
              />
              <p style={{ fontSize: '15px' }}>
                No stock records found for selected filters
              </p>
            </div>
          ) : (
            <>
              <div
                className="d-flex align-items-center justify-content-between px-4 py-3"
                style={{ borderBottom: '1px solid #F1F5F9' }}
              >
                <span
                  className="fw-semibold text-dark"
                  style={{ fontSize: '14px' }}
                >
                  Stock Records
                </span>
                <span
                  className="badge bg-primary rounded-pill"
                  style={{ fontSize: '12px' }}
                >
                  {rows.length} records
                </span>
              </div>

              <div
                className="table-responsive"
                style={{ maxHeight: '65vh', overflowY: 'auto' }}
              >
                <Table
                  hover
                  className="align-middle mb-0"
                  style={{ fontSize: '12.5px' }}
                >
                  <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: '#F8FAFC'
                    }}
                  >
                    <tr>
                      <th
                        className="text-secondary fw-semibold py-3 ps-4"
                        style={{ minWidth: '40px' }}
                      >
                        #
                      </th>
                      {COLUMNS.map(col => (
                        <th
                          key={col}
                          className="text-secondary fw-semibold py-3"
                          style={{ minWidth: '120px', whiteSpace: 'nowrap' }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const catIdx = categories.findIndex(
                        c => c.value === row['product_category_id']
                      );
                      const currentQty = Number(row['Current Stock Qty'] || 0);
                      const stockIn = Number(row['Stock In'] || 0);
                      const stockOut = Number(row['Stock Out'] || 0);

                      return (
                        <tr
                          key={idx}
                          style={{ borderBottom: '1px solid #F8FAFC' }}
                        >
                          <td className="ps-4 text-secondary">{idx + 1}</td>

                          <td className="fw-semibold text-dark">
                            {row['Organization'] || '—'}
                          </td>
                          <td>{row['Warehouse'] || '—'}</td>
                          <td>
                            <div className="fw-semibold text-dark">
                              {row['Product'] || '—'}
                            </div>
                          </td>

                          <td>
                            {row['Product Category'] ? (
                              <Badge
                                bg={catBadge(catIdx >= 0 ? catIdx : idx)}
                                style={{ fontSize: '11px' }}
                              >
                                {row['Product Category']}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </td>

                          <td>
                            {row['Option Name'] ? (
                              <Badge
                                bg="light"
                                text="dark"
                                className="border"
                                style={{ fontSize: '11px' }}
                              >
                                {row['Option Name']}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </td>

                          <td className="text-secondary">
                            {row['Unit'] || '—'}
                          </td>
                          <td className="text-secondary">
                            {row['Lot ID'] || '—'}
                          </td>
                          <td className="text-secondary">
                            {row['Best Before'] || '—'}
                          </td>

                          {/* Stock In */}
                          <td>
                            <span className="fw-semibold text-success">
                              {fmt(stockIn)}
                            </span>
                          </td>

                          {/* Stock Out */}
                          <td>
                            <span className="fw-semibold text-danger">
                              {fmt(stockOut)}
                            </span>
                          </td>

                          {/* Current Stock Qty */}
                          <td>
                            <span
                              className="fw-bold px-2 py-1 rounded"
                              style={{
                                backgroundColor:
                                  currentQty > 0 ? '#DCFCE7' : '#FEE2E2',
                                color: currentQty > 0 ? '#16A34A' : '#DC2626',
                                fontSize: '12px'
                              }}
                            >
                              {fmt(currentQty)}
                            </span>
                          </td>

                          {/* Product Price */}
                          <td className="text-end text-dark">
                            {fmt(row['Product Price'])}
                          </td>

                          {/* Total Stock in Value */}
                          <td className="text-end fw-bold text-dark">
                            {fmtUGX(row['Total Stock in Value'])}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* ── totals footer ── */}
                  <tfoot
                    style={{
                      backgroundColor: '#F1F5F9',
                      borderTop: '2px solid #E2E8F0'
                    }}
                  >
                    <tr>
                      <td
                        colSpan={9}
                        className="fw-bold text-dark ps-4 py-3"
                        style={{ fontSize: '13px' }}
                      >
                        Totals
                      </td>
                      <td className="fw-bold text-success py-3">
                        {fmt(totalStockIn)}
                      </td>
                      <td className="fw-bold text-danger py-3">
                        {fmt(totalStockOut)}
                      </td>
                      <td className="py-3">
                        <span
                          className="fw-bold px-2 py-1 rounded"
                          style={{
                            backgroundColor: '#DCFCE7',
                            color: '#16A34A'
                          }}
                        >
                          {fmt(totalCurrent)}
                        </span>
                      </td>
                      <td></td>
                      <td className="text-end fw-bold text-dark pe-3 py-3">
                        {fmtUGX(totalValue)}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
